

import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import CalculatorForm from './CalculatorForm';
import { motion } from 'framer-motion';

const formatCurrency = (value) => {
  return Number(value).toLocaleString('en-IN');
};

const SWPCalculator = () => {
  const [investment, setInvestment] = useState(1000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(8);
  const [showForm, setShowForm] = useState(false);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState([]);

  const calculate = () => {
    let balance = investment;
    let months = 0;
    let totalWithdrawn = 0;
    const monthlyRate = expectedReturn / 12 / 100;
    const data = [];
    
    while (balance > 0 && months < 600) {
      const interestEarned = balance * monthlyRate;
      balance = balance + interestEarned - monthlyWithdrawal;
      totalWithdrawn += monthlyWithdrawal;
      months++;
      
      if (balance < 0) {
        totalWithdrawn += balance;
        balance = 0;
        break;
      }
      
      if (months % 12 === 0) {
        data.push({
          year: months / 12,
          balance: Math.max(0, balance),
          withdrawn: totalWithdrawn
        });
      }
    }
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const totalInterest = totalWithdrawn - investment;
    
    setResult({ 
      years, 
      remainingMonths, 
      totalWithdrawn, 
      totalInterest,
      finalBalance: balance,
      months,
      futureValue: balance,
      totalInvestment: investment,
      estimatedReturns: totalInterest,
      totalReturns: totalInterest,
    });
    setChartData(data);
  };

  const handleFormSubmit = (userData) => {
    setShowForm(false);
    
    const whatsappMessage = `
Dear ${userData.name},
📧 ${userData.email || 'Not provided'} | 📍 ${userData.location || 'Not provided'}

📊 SWP Investment Summary

💰 Initial Investment: ₹${formatCurrency(investment)}
💵 Monthly Withdrawal: ₹${formatCurrency(monthlyWithdrawal)}
📈 Expected Return: ${expectedReturn}% p.a.

━━━━━━━━━━━━━━━

⏳ Duration: ${result.years} years ${result.remainingMonths} months
💼 Total Withdrawn: ₹${formatCurrency(result.totalWithdrawn)}
📊 Total Interest Earned: ₹${formatCurrency(result.totalInterest)}
${result.finalBalance > 0 ? `🏦 Remaining Corpus: ₹${formatCurrency(result.finalBalance)}` : ''}

━━━━━━━━━━━━━━━

Wisdom Consultancy
📞 +91 9726344477
📧 wisdom2secure@gmail.com
🌐 www.wisdomconsultancy.com
`;
    
    window.open(`https://wa.me/91${userData.phone}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6"
    >
      <h3 className="text-2xl font-bold text-primary mb-2">SWP Calculator</h3>
      <p className="text-gray-600 mb-6">Systematic Withdrawal Plan - Get regular income from your investments</p>
      
      <div className="grid md:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-5">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Total Investment (₹)</label>
            <input
              type="number"
              className="input-field"
              value={investment}
              onChange={(e) => setInvestment(Number(e.target.value))}
            />
            <p className="text-xs text-gray-500 mt-1">Enter the lump sum amount you want to invest</p>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Monthly Withdrawal (₹)</label>
            <input
              type="number"
              className="input-field"
              value={monthlyWithdrawal}
              onChange={(e) => setMonthlyWithdrawal(Number(e.target.value))}
            />
            <p className="text-xs text-gray-500 mt-1">Amount you want to withdraw every month</p>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Expected Return (% p.a.)</label>
            <input
              type="range"
              min="1"
              max="15"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-center font-semibold text-primary mt-1">{expectedReturn}%</div>
          </div>

          <button
            onClick={calculate}
            className="w-full btn-primary py-3 text-lg"
          >
            Calculate SWP
          </button>
        </div>

        {/* Results Section */}
        {result && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5">
            <h4 className="font-bold text-lg text-primary mb-4">SWP Summary</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Initial Investment:</span>
                <span className="font-semibold">₹{investment.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Monthly Withdrawal:</span>
                <span className="font-semibold">₹{monthlyWithdrawal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold text-primary">{result.years} years {result.remainingMonths} months</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Total Withdrawn:</span>
                <span className="font-semibold text-green-600">₹{result.totalWithdrawn.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-gray-700 font-bold">Total Interest Earned:</span>
                <span className="font-bold text-primary text-xl">₹{result.totalInterest.toLocaleString()}</span>
              </div>
            </div>
            
            {result.finalBalance > 0 && (
              <div className="mt-4 p-3 bg-green-100 rounded-lg">
                <p className="text-sm text-green-800">
                  💡 After {result.years} years {result.remainingMonths} months, you'll still have 
                  <strong> ₹{result.finalBalance.toLocaleString()}</strong> remaining!
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chart Section */}
      {chartData.length > 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-8"
        >
          <h4 className="font-bold text-lg text-primary mb-4">Corpus Depletion Chart</h4>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" label={{ value: 'Years', position: 'bottom' }} />
                <YAxis label={{ value: 'Amount (₹)', angle: -90, position: 'left' }} />
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Legend />
                <Line type="monotone" dataKey="balance" stroke="#82ca9d" name="Remaining Corpus" strokeWidth={2} />
                <Line type="monotone" dataKey="withdrawn" stroke="#8884d8" name="Total Withdrawn" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {result && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full mt-6 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-all duration-300"
        >
          📄 Download Report & Share on WhatsApp
        </button>
      )}

      {showForm && (
        <CalculatorForm
          onSubmit={handleFormSubmit}
          onClose={() => setShowForm(false)}
          calculatorType="SWP"
          calculationData={{ ...result, chartData }}
          userInputs={{
            lumpsumAmount: investment,
            monthlyWithdrawal,
            expectedReturn,
          }}
        />
      )}
    </motion.div>
  );
};

export default SWPCalculator;